import { z } from "zod";
import { AuditTrailEntryType } from "../AuditTrailEntryType.js";
/**
 * Shared shape for FHIR API access rows. Deliberately lenient per the registry's
 * rules: these are historical snapshots and must keep reading back as the API
 * grows fields.
 */
const fhirClientAccessSchema = z.object({
    fhirClientId: z.string().nullish(),
    fhirClientName: z.string().nullish(),
});
// Integrations audit `data` schemas (Rio / TPP / Xero / Generic click-through, Vision /
// TPP / LCR / Generic instance config CRUD, Trust + OIDs, Place of Safety, Mental Health
// Care Provider, LCR exclusions / unmerge). See `registry.ts` for the leniency contract.
// Shared empty payload.
export const emptyIntegrationDataSchema = z.object({});
// Shared by updateVisionInstance / updatePosInstance.
export const modifiedFieldsDataSchema = z.object({
    modifiedFields: z.array(z.string()).nullish(),
});
// Shared by createLondonCareRecordExclusion / removeLondonCareRecordExclusion.
export const londonCareRecordExclusionDataSchema = z.object({
    reason: z.string().nullish(), // free-text
});
export const tppClickThroughDataSchema = z.object({
    odsCode: z.string().nullish(),
    tppUsername: z.string().nullish(), // PII — clinical user identity
    requestTime: z.string().nullish(),
});
// Generic EPR click-through (the vendor-neutral equivalent of Rio/TPP). Unlike Rio,
// it persists no apikey/hmac.
export const genericClickThroughDataSchema = z.object({
    odsCode: z.string().nullish(),
    genericUsername: z.string().nullish(), // PII — clinical user identity
    requestTime: z.string().nullish(),
});
// Remembered-login links (EPR username -> eMHA user): bound at redeem (create)
// and cleared by a system admin (delete). eprUsername is PII (clinical user
// identity); create records the username, delete records the affected user id.
export const eprUserLinkDataSchema = z.object({
    source: z.string().nullish(), // "rio" | "tpp" | "generic"
    externalSystemId: z.string().nullish(),
    eprUsername: z.string().nullish(), // PII — clinical user identity
    userId: z.string().nullish(),
});
// TODO(security): `apikey` (a credential) and `hmac` are persisted into audit data
// here, unredacted — stop logging them or add to AuditTrailController.dataFieldsToRedact.
export const rioClickThroughLoginDataSchema = z.object({
    username: z.string().nullish(), // PII
    apikey: z.string().nullish(),
    odscode: z.string().nullish(),
    timestamp: z.string().nullish(),
    hmac: z.string().nullish(),
});
export const xeroConfigurationUpdatedDataSchema = z.object({
    type: z.string().nullish(), // "xero" | "xero-user-contact"
    fields: z.array(z.string()).nullish(),
    userId: z.string().nullish(),
    xeroContactId: z.string().nullish(),
});
// Audit type → schema entries for this file, composed in ../registry.ts.
export const integrationsAuditDataRegistry = {
    // Click-through / config.
    [AuditTrailEntryType.rioClickThroughLaunch]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.rioClickThroughLogin]: rioClickThroughLoginDataSchema,
    [AuditTrailEntryType.tppClickThrough]: tppClickThroughDataSchema,
    [AuditTrailEntryType.tppTokenRedeemed]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.genericClickThrough]: genericClickThroughDataSchema,
    [AuditTrailEntryType.genericTokenRedeemed]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.createEprUserLink]: eprUserLinkDataSchema,
    [AuditTrailEntryType.deleteEprUserLink]: eprUserLinkDataSchema,
    [AuditTrailEntryType.xeroConfigurationUpdated]: xeroConfigurationUpdatedDataSchema,
    // Instances (create/delete audit only the resource; update lists changed fields).
    [AuditTrailEntryType.createVisionInstance]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.updateVisionInstance]: modifiedFieldsDataSchema,
    [AuditTrailEntryType.deleteVisionInstance]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.createTppInstance]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.updateTppInstance]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.deleteTppInstance]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.createGenericInstance]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.updateGenericInstance]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.deleteGenericInstance]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.createLondonCareRecordInstance]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.updateLondonCareRecordInstance]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.deleteLondonCareRecordInstance]: emptyIntegrationDataSchema,
    // London Care Record exclusions / unmerge.
    [AuditTrailEntryType.createLondonCareRecordExclusion]: londonCareRecordExclusionDataSchema,
    [AuditTrailEntryType.removeLondonCareRecordExclusion]: londonCareRecordExclusionDataSchema,
    [AuditTrailEntryType.unmergeLondonCareRecordPatient]: emptyIntegrationDataSchema,
    // Place of Safety.
    [AuditTrailEntryType.deletePosInstance]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.updatePosInstance]: modifiedFieldsDataSchema,
    [AuditTrailEntryType.searchPos]: emptyIntegrationDataSchema,
    // Mental Health Care Provider.
    [AuditTrailEntryType.createMentalHealthCareProvider]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.updateMentalHealthCareProvider]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.deleteMentalHealthCareProvider]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.searchMentalHealthCareProvider]: emptyIntegrationDataSchema,
    // Trust (+ OIDs).
    [AuditTrailEntryType.createTrust]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.updateTrust]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.deleteTrust]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.searchTrust]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.createTrustOid]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.updateTrustOid]: emptyIntegrationDataSchema,
    [AuditTrailEntryType.deleteTrustOid]: emptyIntegrationDataSchema,
    // FHIR API. The actor is a registered FHIR client rather than a user, so the
    // client is recorded in `data` — the row's user columns are null.
    [AuditTrailEntryType.fhirDocumentSearch]: fhirClientAccessSchema.extend({
        nhsNumber: z.string().nullish(),
        documentCount: z.number().nullish(),
    }),
    [AuditTrailEntryType.fhirEpisodeOfCareRead]: fhirClientAccessSchema.extend({
        nhsNumber: z.string().nullish(),
        episodeCount: z.number().nullish(),
    }),
    [AuditTrailEntryType.fhirDocumentRead]: fhirClientAccessSchema.extend({
        formTemplateId: z.string().nullish(),
        formVersion: z.number().nullish(),
    }),
};
//# sourceMappingURL=integrations.js.map